import { useMemo, useState } from "react";
import { DragEndEvent, UniqueIdentifier } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useEditorStore } from "../stores/use-editor-store";
import { PriorityColumn } from "../types/visual-hierarchy.types";
import { useSegment } from "@/hooks/use-segment";
import { Region } from "@/api/studio/data/types/region.type";

/* Priority Levels */
const allPriorityLevels = [
  { id: "0", title: "Unprioritised AOIs", desc: "Drag & drop AOI elements into priority columns. Unprioritised elements will assume lowest priority", priorityValue: 0, items: [] },
  { id: "1", title: "First priority", desc: "I want my audience to see these elements first.", priorityValue: 1, items: [] },
  { id: "2", title: "Second priority", desc: "I want my audience to see these elements second.", priorityValue: 2, items: [] },
  { id: "3", title: "Third priority", desc: "I want my audience to see these elements third.", priorityValue: 3, items: [] },
  { id: "4", title: "Fourth priority", desc: "I want my audience to see these elements fourth.", priorityValue: 4, items: [] },
];

interface useVisualHierarchyProps {
  projectId: string;
  studyId: string;
}

/**
 * resolveGroupPriority function
 *
 * Determines the highest non-zero priority among a group of regions.
 */
const resolveGroupPriority = (regions: Region[]) => {
  const highestNonZero = regions.reduce((max, region) => {
    const priority = region.priority ?? 0;
    return priority > max ? priority : max;
  }, 0);

  return highestNonZero;
};

/**
 * useVisualHierarchy hook
 * This hook manages the visual hierarchy of regions in the editor.
 * It provides functionality to group regions, manage columns, and handle drag-and-drop interactions.
 */
export const useVisualHierarchy = ({ projectId, studyId }: useVisualHierarchyProps) => {
  /* Global State */
  const allAssetRegionData = useEditorStore((state) => state.allAssetRegionData);
  const { setAllAssetRegionData } = useEditorStore((state) => state.editorActions);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  // Group regions by label
  const groupedRegions = useMemo(() => {
    return allAssetRegionData.reduce(
      (acc, asset) => {
        asset.regions?.forEach((region) => {
          if (!acc[region.label]) {
            acc[region.label] = [];
          }
          acc[region.label].push(region);
        });
        return acc;
      },
      {} as Record<string, Region[]>,
    );
  }, [allAssetRegionData]);

  // Map grouped regions to items for the columns, resolving their priority based on contained regions
  const groupedRegionItems = useMemo(() => {
    return Object.entries(groupedRegions).map(([label, regions]) => ({
      label,
      numRegion: regions?.length || 0,
      regionIds: regions?.map((region) => region.id) || [],
      classification: regions?.[0]?.classification ?? "",
      resolvedPriority: resolveGroupPriority(regions || []),
    }));
  }, [groupedRegions]);

  // Add groupedRegions to the first column's items array
  const columnsInit = useMemo(() => {
    // Determine if any grouped label has a non-zero priority set
    const hasPriorities = groupedRegionItems.some((item) => item.resolvedPriority > 0);

    if (!hasPriorities) {
      // No priorities set: put all in AOIs, show only AOIs + First + Second priority columns
      return allPriorityLevels.slice(0, 3).map((col) => {
        if (col.id === "0") {
          // AOIs column: all grouped regions
          return {
            ...col,
            items: groupedRegionItems.map(({ label, numRegion, regionIds, classification }) => ({
              label,
              numRegion,
              regionIds,
              classification,
            })),
          };
        }
        // Other columns: empty
        return { ...col, items: [] };
      });
    } else {
      // Priorities set: distribute grouped regions into columns by their regions' priority
      // Only include columns up to the last non-empty one (after AOIs)
      const columnsWithItems = allPriorityLevels.map((col) => {
        const items = groupedRegionItems
          .filter((item) => item.resolvedPriority === col.priorityValue)
          .map(({ label, numRegion, regionIds, classification }) => ({
            label,
            numRegion,
            regionIds,
            classification,
          }));
        return { ...col, items };
      });

      // Find the last non-empty priority column (excluding AOIs)
      let lastNonEmptyIdx = 0;
      for (let i = 1; i < columnsWithItems.length; i++) {
        if (columnsWithItems[i].items.length > 0) {
          lastNonEmptyIdx = i;
        }
      }
      // Only include columns up to the last non-empty one
      return columnsWithItems.slice(0, lastNonEmptyIdx + 1);
    }
  }, [groupedRegionItems]);

  /* State */
  const [priorityColumns, setPriorityColumns] = useState<PriorityColumn[]>(columnsInit);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  // This state is only used for segment tracking due to dragOver not being able
  // to easily find the column the region started in
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);

  /* Utility Functions */
  const findContainer = (id: UniqueIdentifier | null | undefined) => {
    if (id == null) return null;
    // this can be the id of the column or the label of the item
    if (priorityColumns.some((col) => col.id === id.toString())) {
      return id.toString();
    }
    for (const column of priorityColumns) {
      if (column.items.some((item) => item.label === id.toString())) {
        return column.id;
      }
    }
    return null;
  };

  /*
   * The dragStart handler sets the activeId to the label of the item being dragged.
   * This is used for the DragOverlay to show the item being dragged between columns.
   */
  const handleDragStart = (event: DragEndEvent) => {
    // this sets the label name as the activeId
    const activeId = event.active.id;
    setActiveId(String(activeId));

    // Set the activeColumnId to the column of the item being dragged
    // This is only needed for segment tracking
    const activeContainer = findContainer(activeId);
    if (activeContainer) {
      setActiveColumnId(activeContainer);
    }
  };

  /*
   * The dragOver handler is responsible for moving items between columns
   * as well as reordering items within the same column visually due to the
   * DragOverlay.
   */
  const handleDragOver = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeId = active.id;
    const overId = over?.id;

    // Find the priority column the active item is in
    const activeContainer = findContainer(activeId);
    // Find the priority column the item is being dragged over
    const overContainer = findContainer(overId);

    // If not over anything, do nothing
    if (!activeContainer || !overContainer) {
      return;
    }

    setPriorityColumns((columns) => {
      const sourceColIdx = columns.findIndex((col) => col.id === activeContainer);
      const targetColIdx = columns.findIndex((col) => col.id === overContainer);
      if (sourceColIdx === -1 || targetColIdx === -1) return columns;

      const sourceCol = columns[sourceColIdx];
      const targetCol = columns[targetColIdx];

      const activeIndex = sourceCol.items.findIndex((item) => item && item.label === activeId);
      if (activeIndex === -1) return columns;
      const movedItem = sourceCol.items[activeIndex];
      if (!movedItem || !movedItem.label) return columns;

      // Reorder within the same column
      if (activeContainer === overContainer && overId && activeId !== overId) {
        const overIndex = sourceCol.items.findIndex((item) => item && item.label === overId);
        if (overIndex === -1) return columns;
        return columns.map((col, idx) => {
          if (idx !== sourceColIdx) return col;
          return {
            ...col,
            items: arrayMove(col.items, activeIndex, overIndex).filter(Boolean),
          };
        });
      }

      // Move between columns
      if (activeContainer !== overContainer) {
        const newSourceItems = sourceCol.items.filter((item) => item && item.label !== activeId);
        const overIndex = targetCol.items.findIndex((item) => item && item.label === overId);
        let newTargetItems: typeof targetCol.items;
        if (overIndex === -1) {
          newTargetItems = [...targetCol.items, movedItem];
        } else {
          newTargetItems = [...targetCol.items.slice(0, overIndex), movedItem, ...targetCol.items.slice(overIndex)];
        }
        newTargetItems = newTargetItems.filter(Boolean);
        return columns.map((col, idx) => {
          if (idx === sourceColIdx) {
            return { ...col, items: newSourceItems };
          }
          if (idx === targetColIdx) {
            return { ...col, items: newTargetItems };
          }
          return col;
        });
      }
      return columns;
    });
    setActiveId(String(activeId));
  };

  /*
   * The dragEnd handler mainly handles reordering within the same column as the
   * dragOver handler is responsible for moving items between columns.
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Reset activeId when drag ends
    setActiveId(null);

    if (!over) return;

    // These values should all return the same id
    const activeId = active.id;
    const overId = over.id;
    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId);

    // Only handle reordering within the same column
    if (!activeContainer || !overContainer || activeContainer !== overContainer) {
      return;
    }

    // Find the column index and item indices
    const columnIndex = priorityColumns.findIndex((col) => col.id === activeContainer);
    if (columnIndex === -1) return;

    // See if we need to reorder items within the same column
    const activeIndex = priorityColumns[columnIndex].items.findIndex((item) => item.label === activeId);
    const overIndex = priorityColumns[columnIndex].items.findIndex((item) => item.label === overId);

    // Segment tracking
    segmentTrackEvent("AOI label priority assigned", {
      projectId,
      studyId,
      from: activeColumnId,
      to: overContainer,
      classification: priorityColumns[columnIndex].items[activeIndex]?.classification,
      label: priorityColumns[columnIndex].items[activeIndex]?.label,
    });

    // If indices are invalid or the same, do nothing
    if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) return;

    // Update the priorityColumns state with the new order
    setPriorityColumns((columns) =>
      columns.map((column, idx) => {
        if (idx !== columnIndex) return column;
        return {
          ...column,
          items: arrayMove(column.items, activeIndex, overIndex),
        };
      }),
    );

    // Reset activeColumnId as we have called the segment tracking event
    setActiveColumnId(null);
  };

  const handleAddPriorityLevel = () => {
    // Segment tracking
    segmentTrackEvent("Visual Hierarchy Priority Count Changed", { projectId, studyId, from: priorityColumns.length, to: priorityColumns.length + 1 });

    setPriorityColumns((prev) => {
      // Find the next priority level to add
      const existingIds = prev.map((col) => col.id);
      const nextLevel = allPriorityLevels.find((level) => !existingIds.includes(level.id));

      // Only add if there is a next level
      if (nextLevel) {
        return [...prev, { ...nextLevel, items: [] }];
      }
      return prev;
    });
  };

  const handleRemovePriorityLevel = () => {
    // Segment tracking
    segmentTrackEvent("Visual Hierarchy Priority Count Changed", { projectId, studyId, from: priorityColumns.length, to: priorityColumns.length - 1 });

    setPriorityColumns((prev) => {
      if (prev.length <= 2) return prev;
      const removedCol = prev[prev.length - 1];
      const itemsToMove = removedCol.items;
      // Move items to the first column (AOIs)
      return prev
        .map((col, idx) => {
          if (idx === 0) {
            return { ...col, items: [...col.items, ...itemsToMove] };
          }
          return col;
        })
        .slice(0, -1);
    });
  };

  const handleSetPriority = () => {
    const updatedAssetRegionData = allAssetRegionData.map((asset) => ({
      ...asset,
      regions: asset.regions?.map((region) => {
        // Find the column that contains this region's id
        const column = priorityColumns.find((col) => col.items.some((item) => item.regionIds.includes(region.id)));
        if (column) {
          return { ...region, priority: column.priorityValue };
        }
        // Safety fallback to avoid undefined priorities when legacy data is inconsistent.
        return { ...region, priority: 0 };
      }),
    }));

    setAllAssetRegionData(updatedAssetRegionData);
  };

  /* Return */
  return {
    // State
    priorityColumns,
    activeId,

    // Handlers
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleAddPriorityLevel,
    handleRemovePriorityLevel,
    handleSetPriority,
  };
};
